import { computed, toValue, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import { MOUNT_READY_EVENT } from "#ce/framework/mount-ready";

const SUPPORTS_ELEMENT_REFLECTION = "ariaControlsElements" in Element.prototype;

/**
 * Links an sp-button's inner control to the elements it controls across shadow roots.
 * Restores the link when the host signals mount readiness, including after remount.
 * An absent target clears the relationship; unsupported browsers leave it unset.
 *
 * @param {import('vue').MaybeRefOrGetter<HTMLElement & { controlElement?: Element }|null|undefined>} trigger
 *   The sp-button host exposing its focusable control.
 * @param {import('vue').MaybeRefOrGetter<Element|Element[]|null>} targets - Controlled regions.
 * @returns {void}
 */
export function useAriaControls(trigger, targets) {
  if (!SUPPORTS_ELEMENT_REFLECTION) {
    return;
  }

  const elements = computed(() => {
    const target = toValue(targets);
    if (Array.isArray(target)) {
      return [...target];
    }

    return target ? [target] : [];
  });

  function syncControls() {
    const control = toValue(trigger)?.controlElement;

    if (control) {
      control.ariaControlsElements = elements.value;
    }
  }

  watch([() => toValue(trigger), elements], syncControls, { immediate: true, flush: "post" });
  useEventListener(trigger, MOUNT_READY_EVENT, syncControls);
}
